import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Heart, User, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { LOGO } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "About", children: [
      { to: "/about", label: "Our Mission" },
      { to: "/founder", label: "Founder Story" },
      { to: "/story", label: "Mercy's Story" },
      { to: "/the-need", label: "The Need" },
      { to: "/understanding-ethiopia", label: "Understanding Ethiopia" },
      { to: "/transparency", label: "Transparency" },
    ],
  },
  {
    label: "Programs", children: [
      { to: "/projects", label: "Sponsor a Room" },
      { to: "/community-impact", label: "Community Impact" },
      { to: "/a-better-ethiopia", label: "A Better Ethiopia" },
      { to: "/future-campus", label: "Future Campus" },
    ],
  },
  { label: "Mercy Made", to: "/mercy-made" },
  { label: "Shop", to: "/shop" },
  { label: "Gallery", to: "/gallery" },
  { label: "Get Involved", to: "/join" },
  { label: "Contact", to: "/contact" },
];

const tid = (s) => `nav-${s.toLowerCase().replace(/[^a-z]/g, "-")}`;

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [donate, setDonate] = useState("https://gofund.me/15d8987b7");
  const { pathname } = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    api.get("/settings").then(({ data }) => data.gofundme_url && setDonate(data.gofundme_url)).catch(() => {});
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  const linkCls = (active) => `text-[13px] font-medium transition-colors hover:text-[#C1272D] whitespace-nowrap ${active ? "text-[#C1272D]" : "text-[#4F6B54]"}`;

  return (
    <header className="sticky top-0 z-50 bg-[#FBF8F0]/80 backdrop-blur-xl backdrop-saturate-150 border-b border-[#1F4D2E]/10 shadow-[0_4px_30px_rgba(0,0,0,0.03)]" data-testid="navbar">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-8 h-24 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center group" data-testid="nav-logo">
          <img src={LOGO} alt="Refugium Ethiopia" className="h-16 md:h-20 w-auto drop-shadow-[0_2px_6px_rgba(31,77,46,0.25)] group-hover:scale-105 transition-transform duration-300" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((item) => {
            if (!item.children) {
              return (
                <Link key={item.to} to={item.to} data-testid={tid(item.label)} className={linkCls(pathname === item.to)}>{item.label}</Link>
              );
            }
            const active = item.children.some((c) => c.to === pathname);
            return (
              <div key={item.label} className="relative group">
                <button data-testid={tid(item.label)} className={`${linkCls(active)} inline-flex items-center gap-1`}>
                  {item.label} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 z-50">
                  <div className="bg-white border border-[#1F4D2E]/10 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.12)] py-2 min-w-[220px]" data-testid={`nav-menu-${item.label.toLowerCase()}`}>
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to} data-testid={tid(c.label)}
                        className={`block px-5 py-2.5 text-sm transition-colors hover:bg-[#FBF8F0] ${pathname === c.to ? "text-[#C1272D] font-semibold" : "text-[#1F4D2E]"}`}>{c.label}</Link>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <LanguageSwitcher />
          {user ? (
            <Link to="/account" data-testid="nav-account" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-[#1F4D2E] hover:text-[#C1272D] transition-colors"><User size={17} /> {(user.name || "Account").split(" ")[0]}</Link>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="hidden sm:inline-flex text-sm font-medium text-[#1F4D2E] hover:text-[#C1272D] transition-colors">Sign in</Link>
          )}
          <a href={donate} target="_blank" rel="noopener noreferrer" data-testid="nav-donate-btn"
            className="hidden sm:inline-flex bg-[#C1272D] text-white rounded-full px-6 py-2.5 text-sm font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors">
            Donate
          </a>
          <button className="lg:hidden text-[#1F4D2E]" onClick={() => setOpen(!open)} data-testid="nav-menu-toggle" aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FBF8F0] border-t border-[#1F4D2E]/10 px-6 py-4 flex flex-col max-h-[75vh] overflow-y-auto" data-testid="mobile-menu">
          {NAV.map((item) => (
            item.children ? (
              <div key={item.label} className="py-1">
                <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#C1272D] pt-3 pb-1">{item.label}</p>
                {item.children.map((c) => (
                  <Link key={c.to} to={c.to} className="block py-2 pl-3 text-[#1F4D2E] font-medium border-b border-[#1F4D2E]/5">{c.label}</Link>
                ))}
              </div>
            ) : (
              <Link key={item.to} to={item.to} className="py-2.5 text-[#1F4D2E] font-medium border-b border-[#1F4D2E]/5">{item.label}</Link>
            )
          ))}
          <Link to={user ? "/account" : "/login"} className="py-2.5 text-[#1F4D2E] font-medium border-b border-[#1F4D2E]/5">{user ? "My Account" : "Sign in"}</Link>
          <a href={donate} target="_blank" rel="noopener noreferrer" className="mt-3 bg-[#C1272D] text-white rounded-full px-6 py-3 text-center font-semibold">Donate via GoFundMe</a>
        </div>
      )}
    </header>
  );
}
